import { useState, useRef, useCallback } from 'react';
import { fileService } from '@services/fileService';
import { signingService } from '@services/signingService';
import { useTranslation } from '@hooks/useTranslation';
import styles from './ScanUploader.module.scss';

interface ScanUploaderProps {
  token: string;
  onUploadComplete: (file: File) => void;
}

export function ScanUploader({ token, onUploadComplete }: ScanUploaderProps) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    if (file.type !== 'application/pdf') return t('K_DS_EXT_SCAN_UPLOADER_ERR_PDF_ONLY', 'Only PDF files are accepted.');
    if (file.size > 50 * 1024 * 1024) return t('K_DS_EXT_SCAN_UPLOADER_ERR_SIZE', 'File size must be less than 50 MB.');
    return null;
  }, [t]);

  const handleFile = useCallback((file: File) => {
    const error = validateFile(file);
    if (error) { setUploadError(error); return; }
    setUploadError(null);
    setSelectedFile(file);
    setIsUploaded(false);
  }, [validateFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }, [handleFile]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const fileRef = await fileService.upload(selectedFile);
      await signingService.uploadSignedScan(token, fileRef.id);
      setIsUploaded(true);
      onUploadComplete(selectedFile);
    } catch {
      setUploadError(t('K_DS_EXT_SCAN_UPLOADER_ERR_UPLOAD_FAILED', 'Upload failed. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, token, onUploadComplete, t]);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null); setIsUploaded(false); setUploadError(null);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isUploaded && selectedFile) {
    return (
      <div className={styles.uploadSuccess}>
        <div className={styles.successIcon}>&#10003;</div>
        <p className={styles.successText}>{t('K_DS_EXT_SCAN_UPLOADER_SUCCESS_TEXT', 'Signed document uploaded')}</p>
        <p className={styles.fileName}>{selectedFile.name}</p>
        <button className={styles.btnGhost} onClick={handleRemoveFile}>{t('K_DS_EXT_SCAN_UPLOADER_REPLACE', 'Replace file')}</button>
      </div>
    );
  }

  return (
    <div className={styles.uploader}>
      <div
        className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ''} ${selectedFile ? styles.dropZoneHasFile : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
      >
        <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" className={styles.hiddenInput} onChange={handleInputChange} />
        {selectedFile ? (
          <div className={styles.filePreview}>
            <div className={styles.fileIcon}>PDF</div>
            <div className={styles.fileDetails}>
              <span className={styles.previewName}>{selectedFile.name}</span>
              <span className={styles.previewSize}>{formatFileSize(selectedFile.size)}</span>
            </div>
            <button type="button" className={styles.removeBtn} onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }} aria-label={t('K_DS_EXT_SCAN_UPLOADER_REMOVE_ARIA', 'Remove file')}>&times;</button>
          </div>
        ) : (
          <div className={styles.dropPrompt}>
            <div className={styles.uploadIcon}>&#8593;</div>
            <p className={styles.dropText}>{t('K_DS_EXT_SCAN_UPLOADER_DROP_PROMPT', 'Drag & drop your signed PDF here')}</p>
            <p className={styles.dropHint}>{t('K_DS_EXT_SCAN_UPLOADER_BROWSE', 'or click to browse')}</p>
          </div>
        )}
      </div>
      {uploadError && <p className={styles.errorText}>{uploadError}</p>}
      {selectedFile && !isUploaded && (
        <button className={styles.uploadBtn} onClick={handleUpload} disabled={isUploading}>
          {isUploading ? t('K_DS_EXT_SCAN_UPLOADER_UPLOADING', 'Uploading...') : t('K_DS_EXT_SCAN_UPLOADER_UPLOAD_BTN', 'Upload Signed Document')}
        </button>
      )}
    </div>
  );
}
